import { Card, Flex, Wrap, Badge, Box, Button } from '@chakra-ui/react';
import { toaster, useValidatedSorting } from '@shared';
import { useTranslation } from 'react-i18next';
import { createDataCollectionContext, dbTimeToUserTime } from '@shared';
import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useDrawer } from '@admin';

import { useMeta } from '../../../../api/hooks/useMeta';
import senderNamesService from '../../../../api/services/senderNamesService';
import SenderNameDrawer from './SenderNameDrawer';

const columnHelper = createColumnHelper<SenderName>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function SenderNames() {
	const { t } = useTranslation();
	const { meta } = useMeta('senderNamesService');

	const { openDrawer } = useDrawer();
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableHeader, DataCollectionTableFooter } = useMemo(
		() => createDataCollectionContext<SenderName>(),
		[],
	);

	function getBadgeColorByStatus(status: SenderNameStatus): string {
		switch (status) {
			case 'accepted':
				return 'green';
			case 'rejected':
				return 'red';
			default:
				return 'yellow';
		}
	}

	function getStatusTranslation(status: SenderNameStatus): string {
		switch (status) {
			case 'accepted':
				return t('general.accepted');
			case 'rejected':
				return t('general.rejected');
			case 'processingProspectum':
				return t('general.processing_s');
			case 'processingMyLink':
				return t('general.processing_s');
			default:
				return t('general.unknown');
		}
	}

	const columns = [
		columnHelper.accessor('senderNameID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('name', {
			header: t('general.sender_name'),
			sortingFn: 'basic',
		}),

		columnHelper.accessor('status', {
			header: t('general.status'),
			cell: ({ row }) => (
				<Badge size="md" colorPalette={getBadgeColorByStatus(row.original.status)}>
					{getStatusTranslation(row.original.status)}
				</Badge>
			),
		}),
		columnHelper.accessor('primaryPurpose', {
			header: t('general.primary_purpose'),

			cell: ({ getValue }) => {
				const value = getValue();

				return meta?.primaryPurpose?.[value] ?? value;
			},
		}),
		columnHelper.accessor('secondaryPurpose', {
			header: t('general.secondary_purpose'),

			cell: ({ getValue }) => {
				const value = getValue();
				if (value === null) {
					return '';
				}

				return meta?.primaryPurpose?.[value] ?? value;
			},
		}),
		columnHelper.accessor('tertiaryPurpose', {
			header: t('general.tertiary_purpose'),

			cell: ({ getValue }) => {
				const value = getValue();
				if (value === null) {
					return '';
				}
				return meta?.primaryPurpose?.[value] ?? value;
			},
		}),
		columnHelper.accessor('companyName', {
			header: t('general.company_name'),
		}),
		columnHelper.accessor('contactPersonName', {
			header: t('general.contact_person_name'),
		}),
		columnHelper.accessor('contactEmail', {
			header: t('general.contact_email'),
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			cell: ({ row }) => dbTimeToUserTime(row.original.timeCreated, TIMEZONE),
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			cell: ({ row }) => (row.original.timeModified ? dbTimeToUserTime(row.original.timeModified, TIMEZONE) : null),
		}),
	];

	useValidatedSorting('senderNames', columns);

	return (
		<Wrap gap={4} width={'100%'}>
			<Card.Root maxWidth={'2000px'} width="70%" flex={1} minWidth={'340px'}>
				<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
					<Flex display={'flex'} flexDirection={'column'} gap={2} flex={1} maxWidth={'100%'}>
						<DataCollectionContextProvider
							dataKey="senderNames"
							keyAttribute="senderNameID"
							emptyDataText={t('general.no_sender_names_found')}
							fetchFn={(...args) => senderNamesService.getAllResources(...args)}
							initialItemsPerPage={25}
							columns={columns}
							isServerSide={true}
							isSelectable={false}
							onClickRow={row => {
								if (row.original.status === 'processingProspectum') {
									openDrawer({
										id: 'senderNameDrawer',
										content: <SenderNameDrawer senderNameID={row.original.senderNameID} />,
									});
								} else {
									toaster.create({ type: 'error', title: t('general.sender_name_cannot_be_updated_anymore') });
								}
							}}
						>
							<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
								<DataCollectionTableHeader />
								<Button
									id="addSenderName"
									onClick={() => {
										openDrawer({
											id: 'senderNameDrawer',
											content: <SenderNameDrawer />,
										});
									}}
								>
									{t('general.add_sender_name')}
								</Button>
							</Flex>
							<Box flex={1} overflow="auto" minH={0}>
								<DataCollectionTable />
							</Box>
							<Flex gap={2} flexShrink={0}>
								<DataCollectionTableFooter />
							</Flex>
						</DataCollectionContextProvider>
					</Flex>
				</Card.Body>
			</Card.Root>
		</Wrap>
	);
}
